import React, { useState, useContext, useEffect, useRef } from 'react';
import { WebsiteContext } from '../../context/WebsiteProvider';
import reciprocalIconOn from '../../assets/icons/recipToggleOn.png';
import reciprocalIconOff from '../../assets/icons/recipToggleOff.png';
import reciprocalIconOffLightMode from '../../assets/icons/recipToggleOffLightMode.png';

const AgentSearch = ({ onAgentSelect, darkMode, reciprocalAllowed }) => {
  const {
    searchResults,
    setSearchResults,
    isDropdownOpen,
    setDropdownOpen,
    reciprocal,
    setReciprocal,
    handleChange,
    performSearch,
  } = useContext(WebsiteContext);
  const inputRef = useRef(null);
  const searchDropdownRef = useRef(null);
  const [inputValue, setInputValue] = useState('');

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        searchDropdownRef.current &&
        !searchDropdownRef.current.contains(event.target) &&
        inputRef.current &&
        !inputRef.current.contains(event.target)
      ) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [setDropdownOpen]);

  useEffect(() => {
    if (!isDropdownOpen) {
      setSearchResults([]);
    }
  }, [isDropdownOpen, setSearchResults]);

  useEffect(() => {
    if (!reciprocalAllowed) {
      setReciprocal(false);
    }
  }, [reciprocalAllowed, setReciprocal]);

  useEffect(() => {
    if (inputValue.trim()) {
      performSearch(inputValue, reciprocal);
    }
  }, [reciprocal, performSearch, inputValue]);

  const toggleReciprocal = (event) => {
    event.preventDefault();
    event.stopPropagation();
    const newReciprocalState = !reciprocal;
    setReciprocal(newReciprocalState);
    if (inputValue.trim()) {
      performSearch(inputValue, newReciprocalState);
      setDropdownOpen(true);
    }
  };

  const handleInputChange = (e) => {
    setInputValue(e.target.value);
    handleChange(e);
    if (e.target.value.trim()) {
      setDropdownOpen(true);
    } else {
      setDropdownOpen(false);
      setSearchResults([]);
    }
  };

  const handleSelectAgent = (agent) => {
    onAgentSelect(agent);
    setInputValue('');
    setTimeout(() => {
      setDropdownOpen(false);
      if (inputRef.current) {
        inputRef.current.blur();
      }
    }, 10);
  };

  const getIcon = () => {
    if (reciprocal) {
      return reciprocalIconOn;
    } else {
      return darkMode ? reciprocalIconOff : reciprocalIconOffLightMode;
    }
  };

  return (
    <div className="relative">
      <input
        name="agentSearch"
        ref={inputRef}
        className="website-input"
        type="text"
        placeholder={
          reciprocalAllowed && reciprocal
            ? 'Search for Reciprocal Agents'
            : 'Search for Agents'
        }
        value={inputValue}
        onChange={handleInputChange}
        onFocus={() => {
          if (inputValue.trim()) {
            setDropdownOpen(true);
          }
        }}
      />
      {reciprocalAllowed && (
        <button
          onClick={toggleReciprocal}
          className={`absolute inset-y-0 right-0 m-1 flex items-center rounded border border-gray-400 px-1 dark:border-stone-500 ${
            reciprocal
              ? 'bg-gray-200 dark:bg-stone-700'
              : 'bg-gray-300 dark:bg-stone-500'
          }`}
          title={
            reciprocal
              ? 'Searching Reciprocal Agents'
              : 'Searching Non-Reciprocal Agents'
          }
        >
          <img src={getIcon()} alt="Toggle Reciprocal" className="h-5 w-5" />
        </button>
      )}
      {isDropdownOpen && searchResults.length > 0 && (
        <div
          ref={searchDropdownRef}
          className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded border border-gray-200 bg-white dark:border-stone-700 dark:bg-stone-800 dark:text-gray-300"
        >
          {searchResults.map((result, index) => (
            <div
              key={index}
              className="cursor-pointer p-2 hover:bg-gray-200 dark:hover:bg-stone-600"
              onClick={() => handleSelectAgent(result)}
            >
              <div className="flex flex-wrap text-sm">
                <div className="w-full">
                  <span className="font-semibold text-gray-700 dark:text-gray-300">
                    Name:
                  </span>
                  <span className="text-gray-600 dark:text-gray-300">{` ${result.firstName} ${result.lastName}`}</span>
                </div>
                <div className="w-full">
                  <span className="font-semibold text-gray-700 dark:text-gray-300">
                    MLS ID:
                  </span>
                  <span className="text-gray-600 dark:text-gray-300">{` ${result.mlsId || 'N/A'}`}</span>
                </div>
                <div className="w-full">
                  <span className="font-semibold text-gray-700 dark:text-gray-300">
                    Member#:
                  </span>
                  <span className="text-gray-600 dark:text-gray-300">{` ${result.memberNumber || 'N/A'}`}</span>
                </div>
                <div className="w-full">
                  <span className="font-semibold text-gray-700 dark:text-gray-300">
                    Office:
                  </span>
                  <span className="text-gray-600 dark:text-gray-300">{` ${result.office || 'N/A'}`}</span>
                </div>
                <div className="w-full">
                  <span className="font-semibold text-gray-700 dark:text-gray-300">
                    DRE#:
                  </span>
                  <span className="text-gray-600 dark:text-gray-300">{` ${result.licenseNumber || 'N/A'}`}</span>
                </div>
                {reciprocal && (
                  <div className="w-full">
                    <span className="font-semibold text-gray-700 dark:text-gray-300">
                      AOR:
                    </span>
                    <span className="text-gray-600 dark:text-gray-300">{` ${result.memberAOR || 'N/A'}`}</span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AgentSearch;